import { byId, $, $$ } from "@utils/dom";
import { openDialog } from "@components/dialog";
import { buildFilePaths } from "@services/storage/filenames";
import { downloadZipFromR2 } from "@services/download/download";
import {
  incrementDownloadCounts,
  toggleElementFavorite,
} from "@services/api/element";
import { DATA_ATTR } from "@constants/data-attributes";
import type { ElementSelectData } from "@app-types/element.types";

/**
 * Handle element download button click
 * Downloads a single element's files as a zip
 */
async function handleElementDownload(event: MouseEvent): Promise<void> {
  const button = event.currentTarget as HTMLButtonElement;

  // Get icon element for swapping

  const icon = button.querySelector("svg use");
  const iconOuter = button.querySelector("svg");
  const originalIcon =
    icon?.getAttribute("href") ||
    icon?.getAttributeNS("http://www.w3.org/1999/xlink", "href");

  // Disable button and update styles during download
  button.disabled = true;
  button.classList.remove("cursor-pointer");
  button.classList.add("opacity-50");

  // Swap icon to spinner
  if (icon && iconOuter) {
    icon.setAttribute("href", "#iconSpinner");
    iconOuter.classList.add("spinner");
  }

  // Get element data from select-element button on the same page
  const selectButton = $<HTMLElement>("[data-select-element]");
  if (!selectButton) {
    button.disabled = false;
    button.classList.add("cursor-pointer");
    button.classList.remove("opacity-50");
    if (icon && originalIcon) {
      icon.setAttribute("href", originalIcon);
    }
    return;
  }

  const groupId = selectButton.dataset.groupId ?? "";
  const groupName = selectButton.dataset.groupName ?? "";

  const data: ElementSelectData = {
    category: selectButton.dataset.category ?? "",
    groupName: groupName,
    elementId: selectButton.dataset.elementId ?? "",
    isHd: (selectButton.dataset.isHd as "0" | "1") ?? "0",
    isAnimated: (selectButton.dataset.isAnimated as "0" | "1") ?? "0",
    isMultiple: (selectButton.dataset.isMultiple as "0" | "1") ?? "0",
    frames: selectButton.dataset.frames ?? "0",
  };

  // Get filenames from hidden inputs
  const filenameInputs = $$<HTMLInputElement>('input[name="filenames[]"]');
  const filenames = filenameInputs.map((input) => input.value);

  // Build file paths using existing utility
  const filePaths = buildFilePaths(data, filenames);
  const filenamesStorage = { [groupId]: filePaths };

  try {
    await downloadZipFromR2(
      filenamesStorage,
      `${groupName}.zip`,
      undefined,
      null,
      true,
    );

    // Increment download count (fire and forget)
    incrementDownloadCounts([data.elementId]);
  } catch (error) {
    console.error("Element download failed:", error);
  } finally {
    // Restore button state
    button.disabled = false;
    button.classList.add("cursor-pointer");
    button.classList.remove("opacity-50");
    if (icon && originalIcon) {
      iconOuter?.classList.remove("spinner");
      icon.setAttribute("href", originalIcon);
    }
  }
}

/**
 * Handle Favorite button click
 * Shows login dialog if user is a guest, toggles favorite state if logged in
 */
async function handleElementFavorite(event: MouseEvent): Promise<void> {
  // Show login dialog for guests
  const notLoggedInDialog = byId("notLoggedInDialog");
  if (notLoggedInDialog) {
    await openDialog("notLoggedInDialog");
    return;
  }

  const button = event.currentTarget as HTMLButtonElement;
  const elementId = button.dataset.favoriteElement;

  if (!elementId) {
    console.error("Missing element ID for favorite button");
    return;
  }

  // Get icon element for swapping
  const icon = button.querySelector("svg use");
  const iconOuter = button.querySelector("svg");
  const originalIcon =
    icon?.getAttribute("href") ||
    icon?.getAttributeNS("http://www.w3.org/1999/xlink", "href");
  const wasFavorited = button.dataset.isFavorited === "true";

  // Disable button and show spinner
  button.disabled = true;
  button.classList.remove("cursor-pointer");
  button.classList.add("opacity-50");

  if (icon && iconOuter) {
    icon.setAttribute("href", "#iconSpinner");
    iconOuter.classList.add("spinner");
  }

  try {
    const result = await toggleElementFavorite(elementId);

    // Update button state
    button.dataset.isFavorited = result.isFavorited ? "true" : "false";

    // Swap icon based on new state
    if (icon) {
      const newIcon = result.isFavorited ? "#iconStar" : "#iconStarOutline";
      icon.setAttribute("href", newIcon);
    }

    // Update button title text
    const titleSpan = button.querySelector("span");
    if (titleSpan) {
      titleSpan.textContent = result.isFavorited ? "in favorites" : "favorite";
    }

    // Update favorite count if displayed
    const countElement = $<HTMLElement>("[data-favorite-count]");
    if (countElement) {
      countElement.textContent = result.favoriteCount.toString();
    }
  } catch (error) {
    console.error("Favorite toggle failed:", error);

    // Restore original icon on error
    if (icon && originalIcon) {
      icon.setAttribute("href", originalIcon);
    }
    // Restore original state
    button.dataset.isFavorited = wasFavorited ? "true" : "false";
  } finally {
    // Restore button state
    button.disabled = false;
    button.classList.add("cursor-pointer");
    button.classList.remove("opacity-50");
    if (iconOuter) {
      iconOuter.classList.remove("spinner");
    }
  }
}

/**
 * Initialize element page options functionality
 * Attaches click handler to all download and favorite element buttons
 */
export function initElementOptions(): void {
  const downloadButtons = $$(DATA_ATTR.DOWNLOAD_ELEMENT);
  const favoriteButtons = $$(DATA_ATTR.FAVORITE_ELEMENT);

  downloadButtons.forEach((button) => {
    button.addEventListener("click", handleElementDownload);
  });

  favoriteButtons.forEach((button) => {
    button.addEventListener("click", handleElementFavorite);
  });
}
